import React, { createContext, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, useColorScheme } from '@mui/material/styles';
import { appTheme } from './appTheme';
import type { ThemeMode, ThemeContextType } from './types';

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const getInitialMode = (): ThemeMode => {
  const saved = localStorage.getItem('theme');
  if (saved === 'light' || saved === 'dark') return saved;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const ThemeSync: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { colorScheme, setMode: setMuiMode } = useColorScheme();
  const resolvedMode: ThemeMode = colorScheme === 'dark' ? 'dark' : 'light';

  useEffect(() => {
    const handleThemeChange = () => {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme === 'light' || savedTheme === 'dark') setMuiMode(savedTheme);
    };
    window.addEventListener('theme:changed', handleThemeChange);
    window.addEventListener('storage', handleThemeChange);
    return () => {
      window.removeEventListener('theme:changed', handleThemeChange);
      window.removeEventListener('storage', handleThemeChange);
    };
  }, [setMuiMode]);

  const setMode = useCallback(async (newMode: ThemeMode) => {
    localStorage.setItem('theme', newMode);
    setMuiMode(newMode);
    window.dispatchEvent(new Event('theme:changed'));
  }, [setMuiMode]);

  const toggleTheme = useCallback(() => {
    setMode(resolvedMode === 'light' ? 'dark' : 'light');
  }, [resolvedMode, setMode]);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', resolvedMode === 'dark');
    document.documentElement.classList.toggle('light', resolvedMode === 'light');
  }, [resolvedMode]);

  const contextValue = useMemo(() => ({
    mode: resolvedMode,
    toggleTheme,
    setMode,
  }), [resolvedMode, toggleTheme, setMode]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => (
  <MuiThemeProvider theme={appTheme} defaultMode={getInitialMode()} modeStorageKey="theme">
    <ThemeSync>{children}</ThemeSync>
  </MuiThemeProvider>
);
